import type {
  SessionMetricPoint,
  StepAcquisitionSequencePoint,
  TargetTrendSummary,
  TargetVisualAnalysis,
} from "api/types/charts";
import type { ToggleOption } from "../shared/ToggleChartGroup";
import { getMetricChartConfig, hasLineMetricValue } from "../Targets/metricChartRegistry";
import { buildDistributionData } from "./buildDistributionData";
import {
  deriveChainCompletion,
  deriveMasteryStatus,
  toBannerCriteria,
} from "./deriveStatusInsights";
import { directionsForTarget, type MasteryBannerPhase } from "./evaluateMasteryBanner";

/** Segment ids whose card is a categorical breakdown built by `buildDistributionData`. */
export const DISTRIBUTION_SEGMENT_IDS = new Set([
  "prompt-level-distribution",
  "step-level-errors",
  "rating-distribution",
  "latency-by-prompt-level",
]);

export const STEP_ACQUISITION_SEGMENT_ID = "step-acquisition-sequence";

export interface ChartOptionDataContext {
  sessionMetrics: SessionMetricPoint[];
  stepAcquisitionSequence?: StepAcquisitionSequencePoint[];
  /** Target type — drives mastery/regression comparison direction. */
  targetType?: string | null;
  /** Target's L1 behavioral dimension — also drives mastery/regression comparison direction. */
  l1Dimension?: string | null;
  /** Target clinical phase — tweaks mastery-status copy (generalization). */
  phase?: MasteryBannerPhase;
  /** Target's stored mastery criteria JSON. */
  masteryCriteria?: unknown;
  /** Server-computed trend classification — presence alone determines has-data. */
  trend?: TargetTrendSummary | null;
  /** Server-computed visual-analysis trifecta — presence alone determines has-data. */
  visualAnalysis?: TargetVisualAnalysis | null;
  /** Server-computed phase-bounded "Trials/Opportunities to Criterion" aggregate. */
  trialsToCriterion?: number | null;
}

/**
 * Whether `option` would render actual content given the target's current
 * session data — the single has-data predicate shared by every surface that
 * offers a chart-toggle list (`TargetDetail`, `GoalProgressCard`), so no UI
 * ever presents a tab that immediately renders an empty state.
 *
 * Mirrors, per segment, exactly what `TargetChartArea` computes to render
 * that segment's card/chart — a card counts as "has data" only when its
 * `derive*` function would return non-null, not merely when raw session
 * data exists (e.g. a mastery-status card also needs a configured threshold).
 */
export function hasDataForChartOption(
  option: ToggleOption,
  ctx: ChartOptionDataContext
): boolean {
  const {
    sessionMetrics,
    stepAcquisitionSequence = [],
    targetType,
    l1Dimension,
    phase,
    masteryCriteria,
    trend,
    visualAnalysis,
    trialsToCriterion,
  } = ctx;

  if (option.id === STEP_ACQUISITION_SEGMENT_ID) {
    return stepAcquisitionSequence.length > 0;
  }

  if (option.id !== undefined && DISTRIBUTION_SEGMENT_IDS.has(option.id)) {
    return buildDistributionData(option.id, sessionMetrics) !== null;
  }

  if (option.id === "trend") {
    return trend != null;
  }

  if (option.id === "trials-to-criterion") {
    return trialsToCriterion != null;
  }

  if (option.id === "mastery-status") {
    const { masteryDirection } = directionsForTarget({ l1Dimension, targetType });
    return (
      deriveMasteryStatus({
        metrics: sessionMetrics,
        masteryCriteria: toBannerCriteria(masteryCriteria, l1Dimension),
        direction: masteryDirection,
        phase,
      }) !== null
    );
  }

  if (option.id === "chain-completion") {
    return deriveChainCompletion({ metrics: sessionMetrics }) !== null;
  }

  if (option.id === "trend-level-variability") {
    return visualAnalysis != null;
  }

  if (option.id === undefined) {
    const config = getMetricChartConfig(option.label);
    return (
      !!config &&
      config.kind !== "distribution" &&
      sessionMetrics.some((metric) => hasLineMetricValue(metric, config))
    );
  }

  // Unrecognised segment id — keep it rather than silently hiding an option
  // this predicate doesn't yet know how to evaluate.
  return true;
}
